import { Controller } from "@hotwired/stimulus";
import { PreviewMessageService } from "../services/preview_message_service";
import { DefaultsSaveService } from "../services/defaults_save_service";

/**
 * TemplateDesignerController — Coordinates the template designer UI.
 *
 * Thin controller: delegates to PreviewMessageService (iframe communication)
 * and DefaultsSaveService (debounced persistence).
 */
export default class TemplateDesignerController extends Controller {
  static targets = [
    "previewIframe",
    "variantSelect",
    "variantList",
    "groupContent",
    "groupChevron",
    "rangeValue",
  ];

  static values = {
    templateId: String,
    compositionId: String,
    previewUrl: String,
    saveUrl: String,
  };

  connect() {
    this._csrfToken =
      document.querySelector('meta[name="csrf-token"]')?.content || "";

    // Initialize services once iframe is ready
    if (this.hasPreviewIframeTarget) {
      this.previewService = new PreviewMessageService(
        this.previewIframeTarget
      );
      // Sync saved design_data to preview once iframe's React app mounts
      this.previewIframeTarget.addEventListener(
        "load",
        () => setTimeout(() => this._syncPreviewProps(), 300),
        { once: true }
      );
    }

    this.saveService = new DefaultsSaveService(
      this.saveUrlValue,
      this._csrfToken
    );

    // Set initial visibility for fields with depends_on
    this._initDependentFields();
  }

  disconnect() {
    this.saveService?.flush({ keepalive: true });
  }

  // --- Property editing ---

  updateProp(event) {
    const input = event.target;
    const remotionKey = input.dataset.remotionKey;
    if (!remotionKey) return;

    let value = input.value;

    // Coerce number types
    if (input.type === "range" || input.type === "number") {
      value = parseFloat(value);
    }

    // Update preview in real-time
    this.previewService?.updateProp(remotionKey, value);

    // Queue save to DB
    this.saveService.queueSave(remotionKey, value);

    // Update range display
    if (input.type === "range") {
      const display = input
        .closest(".space-y-1")
        ?.querySelector('[data-template-designer-target="rangeValue"]');
      if (display) {
        const unit = display.textContent.replace(/[\d.]+/, "").trim();
        display.textContent = `${value}${unit}`;
      }
    }

    // Toggle dependent field visibility
    this._updateDependentFields(remotionKey, value);

    // Sync color text input with color picker
    if (input.type === "color") {
      const textInput = input.parentElement?.querySelector('input[type="text"]');
      if (textInput) textInput.value = value;
    } else if (
      input.type === "text" &&
      input.pattern === "#[0-9A-Fa-f]{6}"
    ) {
      const colorInput = input.parentElement?.querySelector(
        'input[type="color"]'
      );
      if (colorInput && /^#[0-9A-Fa-f]{6}$/.test(value)) {
        colorInput.value = value;
      }
    }
  }

  updateBoolProp(event) {
    const input = event.target;
    const remotionKey = input.dataset.remotionKey;
    if (!remotionKey) return;

    const value = input.checked;
    this.previewService?.updateProp(remotionKey, value);
    this.saveService.queueSave(remotionKey, value);
  }

  // --- Group collapsing ---

  toggleGroup(event) {
    const button = event.currentTarget;
    const container = button.parentElement;
    const content = container.querySelector(
      '[data-template-designer-target="groupContent"]'
    );
    const chevron = container.querySelector(
      '[data-template-designer-target="groupChevron"]'
    );

    if (content) {
      content.classList.toggle("hidden");
    }
    if (chevron) {
      chevron.classList.toggle("rotate-180");
    }
  }

  // --- Section hover → seek preview ---

  hoverSection(event) {
    const sectionId = event.currentTarget.dataset.sectionId;
    const time = parseFloat(event.currentTarget.dataset.previewTime);
    if (isNaN(time)) return;

    // Only seek when entering a different section than the one we last visited
    if (sectionId && sectionId === this._lastHoveredSection) return;

    this._lastHoveredSection = sectionId || null;
    const fps = 30;
    this.previewService?.seekToFrame(Math.round(time * fps));
  }

  leaveSection() {
    // No-op — leave the preview paused at the seeked frame so the user can see it
  }

  // --- Export: flush all saves (pending + in-flight) before form submission ---

  async beforeExport(event) {
    event.preventDefault();
    // Wait for ALL saves (pending data AND any in-flight PATCH) to complete
    await this.saveService.flush();
    // Now safe to submit — all data is committed to DB
    event.target.submit();
  }

  // --- Variant management ---

  switchVariant(event) {
    const variantId = event.target.value;
    // Tell preview to apply variant overrides
    if (variantId) {
      this.previewService?.updateProp("variant", variantId);
    } else {
      this.previewService?.resetProps();
    }
  }

  addVariant() {
    const name = prompt("Variant name:");
    if (!name) return;

    const newVariant = {
      id: name.toLowerCase().replace(/\s+/g, "-"),
      name: name,
      backgroundConfig: {
        type: "gradient",
        gradientStart: "#0F172A",
        gradientEnd: "#0B1120",
        gradientDirection: 180,
      },
      textColor: "#FFFFFF",
      accentColor: "#6366F1",
    };

    // Save via backend — full page reload to update variant list
    this._saveVariantAction([newVariant], "add");
  }

  duplicateVariant(event) {
    const index = parseInt(event.target.dataset.variantIndex || event.currentTarget.dataset.variantIndex);
    // Trigger via Turbo to reload with duplicated variant
    window.location.href = `${this.saveUrlValue}?duplicate_variant=${index}`;
  }

  removeVariant(event) {
    const index = parseInt(event.target.dataset.variantIndex || event.currentTarget.dataset.variantIndex);
    if (!confirm("Remove this variant?")) return;
    window.location.href = `${this.saveUrlValue}?remove_variant=${index}`;
  }

  previewVariant(event) {
    const index = parseInt(event.target.dataset.variantIndex || event.currentTarget.dataset.variantIndex);
    // Set the variant dropdown to this index
    if (this.hasVariantSelectTarget) {
      this.variantSelectTarget.selectedIndex = index + 1; // +1 for "Default" option
      this.variantSelectTarget.dispatchEvent(new Event("change"));
    }
  }

  async _saveVariantAction(variants, action) {
    try {
      await this.saveService.saveVariants(variants);
      window.location.reload();
    } catch (e) {
      console.error(`Variant ${action} failed:`, e);
    }
  }

  // --- Initial preview sync (send saved design_data on page load) ---

  _syncPreviewProps() {
    if (!this.previewService) return;

    const seen = new Set();
    document.querySelectorAll("[data-remotion-key]").forEach((input) => {
      const key = input.dataset.remotionKey;
      if (!key || seen.has(key)) return;
      seen.add(key);

      let value;
      if (input.type === "checkbox") {
        value = input.checked;
      } else if (input.type === "range" || input.type === "number") {
        value = parseFloat(input.value);
      } else {
        value = input.value;
      }

      this.previewService.updateProp(key, value);
    });
  }

  // --- Conditional field visibility (depends_on) ---

  _updateDependentFields(remotionKey, newValue) {
    // Convert remotionKey to snake_case key for matching depends_on attributes
    const snakeKey = remotionKey
      .replace(/[A-Z]/g, (m) => "_" + m.toLowerCase())
      .replace(/^_/, "");

    document
      .querySelectorAll(`[data-depends-on-key="${snakeKey}"]`)
      .forEach((el) => {
        const allowedValues = (el.dataset.dependsOnValues || "").split(",");
        el.style.display = allowedValues.includes(String(newValue))
          ? ""
          : "none";
      });
  }

  _initDependentFields() {
    document.querySelectorAll("[data-depends-on-key]").forEach((el) => {
      const dependsKey = el.dataset.dependsOnKey;
      const allowedValues = (el.dataset.dependsOnValues || "").split(",");

      // Convert snake_case depends key to camelCase remotion key
      const remotionKey = dependsKey.replace(/_([a-z])/g, (_, c) =>
        c.toUpperCase()
      );

      const depInput = document.querySelector(
        `[data-remotion-key="${remotionKey}"]`
      );
      if (depInput) {
        el.style.display = allowedValues.includes(depInput.value)
          ? ""
          : "none";
      }
    });
  }
}
